import {includes, isEmpty, keyBy, map, max, orderBy, without} from 'lodash';
import moment from 'moment';
import {Fragment, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {TooltipProps} from 'recharts';

import {complianceAuditApi} from 'api';
import {Card, Chart, DataWrapper, Select, Table, TableBody, TableCell, TableRow, notifier} from 'components';
import {themeConfig} from 'constants/theme';
import {UserContext} from 'context';
import {useApiData} from 'hooks';
import {SessionActivityName} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {ComplianceChartMetric, ComplianceStatistic, UserSummary} from 'types';
import {dateUtils} from 'utils';
import {getPercentage} from 'utils/transform';

import {ActivityItem} from '../ActivityItem';
import {ActivityFilters} from '../types';

import {complianceUtils} from './utils';

const T_KEYS = TRANSLATION_KEYS.USER.ACTIVITY.COMPLIANCE;
const T_KEYS_STATISTICS_FAILED = TRANSLATION_KEYS.USER.ACTIVITY.ERROR.STATISTICS_FAILED;

const DAILY_DATE_FORMAT = 'YYYY-MM-DD';

const COLORS = {
    CHECKS: themeConfig.colors.accentCyan,
    PASSED: themeConfig.colors.lightGreen,
    FLAGGED: themeConfig.colors.lightRed,
    IGNORED: themeConfig.colors.orange,
    SKIPPED: themeConfig.colors.lightGray
};

const DailyChecksTooltip = (props: TooltipProps) => {
    if (!props?.active || isEmpty(props?.payload)) {
        return null;
    }
    return (
        <div className='tw-z-1 tw-bg-light tw-p-4'>
            <p>{props.label}</p>
            {map(props.payload, (item) => (
                <p key={String(item.dataKey)}>{`${item.name}: ${item.value}`}</p>
            ))}
        </div>
    );
};

// Non-empty sentinel: Select renders the selected option by id, an empty id shows as a blank field.
const ALL_ACTIVITIES = 'all';

type Props = {
    filters: ActivityFilters | null;
    users: UserSummary[];
    isLoading: boolean;
    onToggleLoading: (value: boolean) => void;
};

export const ComplianceAnalytics = ({filters, users, isLoading, onToggleLoading}: Props) => {
    const {t} = useTranslation();
    const {settings: {common: {dateFormat}}} = useContext(UserContext);

    const [activityFilter, setActivityFilter] = useState<SessionActivityName | typeof ALL_ACTIVITIES>(ALL_ACTIVITIES);
    const activityName = useMemo(
        () => (activityFilter === ALL_ACTIVITIES ? undefined : activityFilter),
        [activityFilter]
    );
    const activityOptions = useMemo(() => [
        {id: ALL_ACTIVITIES, name: t(T_KEYS.ALL_ACTIVITIES)},
        {id: SessionActivityName.TextChat, name: t(TRANSLATION_KEYS.FEATURES.SESSION.ACTIVITY.TEXT_CHAT)},
        {id: SessionActivityName.Audio, name: t(TRANSLATION_KEYS.FEATURES.SESSION.ACTIVITY.AUDIO)},
        {id: SessionActivityName.AudioVideo, name: t(TRANSLATION_KEYS.FEATURES.SESSION.ACTIVITY.AUDIO_VIDEO)}
    ], [t]);

    // Audio compliance stores only flagged hints (no gate) — checks/ignored/skipped carry no signal there.
    const isAudioOnly = useMemo(
        () => includes([SessionActivityName.Audio, SessionActivityName.AudioVideo], activityName),
        [activityName]
    );

    const onError = useCallback(() => {
        notifier.error({title: t(T_KEYS_STATISTICS_FAILED)});
    }, [t]);

    const loadStatisticFn = useCallback((): Promise<ComplianceStatistic> => {
        if (!filters) {
            return Promise.resolve(null);
        }
        return complianceAuditApi.getStatistic({
            ...complianceUtils.toComplianceAuditFilters(filters),
            ...(activityName && {activityName})
        });
    }, [filters, activityName]);

    const {send: loadStatistic, data: statistic, loading} = useApiData(loadStatisticFn, {onError});

    useEffect(() => {
        if (filters) {
            loadStatistic();
        }
    }, [loadStatistic, filters]);

    useEffect(() => {
        if (isLoading !== loading) {
            onToggleLoading(loading);
        }
    }, [loading, isLoading, onToggleLoading]);

    const [selectedMetrics, setSelectedMetrics] = useState<ComplianceChartMetric[]>([
        ComplianceChartMetric.Checks, ComplianceChartMetric.Flagged
    ]);

    const toggleMetric = useCallback((metric: ComplianceChartMetric) => {
        setSelectedMetrics((prev) => (
            includes(prev, metric) ? without(prev, metric) : [...prev, metric]
        ));
    }, []);

    const metrics = useMemo(() => {
        const totals = statistic?.totals;
        if (isAudioOnly) {
            return [{
                metric: ComplianceChartMetric.Flagged,
                title: T_KEYS.METRICS.FLAGGED_RATE,
                color: COLORS.FLAGGED,
                value: totals?.flagged ?? 0
            }];
        }
        return [
            {metric: ComplianceChartMetric.Checks, title: T_KEYS.METRICS.CHECKS, color: COLORS.CHECKS, value: totals?.checks ?? 0},
            {
                metric: ComplianceChartMetric.Flagged,
                title: T_KEYS.METRICS.FLAGGED_RATE,
                color: COLORS.FLAGGED,
                value: totals?.flagged ?? 0,
                percent: getPercentage(totals?.flagged ?? 0, totals?.checks ?? 0)
            },
            {
                metric: ComplianceChartMetric.Ignored,
                title: T_KEYS.METRICS.IGNORE_RATE,
                color: COLORS.IGNORED,
                value: totals?.ignored ?? 0,
                percent: getPercentage(totals?.ignored ?? 0, totals?.flagged ?? 0)
            },
            {
                metric: ComplianceChartMetric.Errors,
                title: T_KEYS.METRICS.CHECKS_SKIPPED,
                color: COLORS.SKIPPED,
                value: totals?.errors ?? 0,
                percent: getPercentage(totals?.errors ?? 0, totals?.checks ?? 0)
            }
        ];
    }, [statistic, isAudioOnly]);

    // Fill the WHOLE filter range with zero days (mirrors the Detailed view chart) — the API returns
    // only days that have records, and a sparse line over the full axis reads wrong without the gaps.
    const chartData = useMemo(() => {
        const dailyByDate = keyBy(statistic?.daily, 'date');
        const dates = filters
            ? dateUtils.getRange(filters.startDate, filters.endDate, 'days', DAILY_DATE_FORMAT, {includeEndDate: true})
            : [];
        return map(dates, (date) => ({
            date: moment(date, DAILY_DATE_FORMAT).format(dateFormat),
            [ComplianceChartMetric.Checks]: dailyByDate[date]?.checks ?? 0,
            [ComplianceChartMetric.Flagged]: dailyByDate[date]?.flagged ?? 0,
            [ComplianceChartMetric.Ignored]: dailyByDate[date]?.ignored ?? 0,
            [ComplianceChartMetric.Errors]: dailyByDate[date]?.errors ?? 0
        }));
    }, [statistic, filters, dateFormat]);

    // All lines stay mounted; a deselected metric just gets an empty dataKey — unmounting a Line makes
    // recharts replay the whole chart animation on every card click (mirrors ActivityMetricsChart).
    const lines = useMemo(() => map(metrics, ({metric, title, color}) => ({
        type: 'linear' as const,
        dataKey: includes(selectedMetrics, metric) ? metric : '',
        name: t(title),
        stroke: color,
        dot: false
    })), [metrics, selectedMetrics, t]);

    const rules = useMemo(() => orderBy(statistic?.rules, 'flagged', 'desc'), [statistic]);
    const maxRuleFlagged = useMemo(() => max(map(rules, 'flagged')) || 0, [rules]);

    const agentsHeaders = useMemo(() => (isAudioOnly
        ? [
            {children: t(T_KEYS.AGENTS.COLUMNS.AGENT)},
            {children: t(T_KEYS.METRICS.FLAGGED_RATE), className: 'tw-w-32'}
        ]
        : [
            {children: t(T_KEYS.AGENTS.COLUMNS.AGENT)},
            {children: t(T_KEYS.METRICS.CHECKS), className: 'tw-w-28'},
            {children: t(T_KEYS.METRICS.FLAGGED_RATE), className: 'tw-w-32'},
            {children: t(T_KEYS.METRICS.IGNORE_RATE), className: 'tw-w-32'},
            {children: t(T_KEYS.METRICS.CHECKS_SKIPPED), className: 'tw-w-32'}
        ]), [t, isAudioOnly]);

    if (!filters) {
        return null;
    }

    return (
        <div className='tw-relative tw-min-h-32'>
            <div className='tw-w-64 tw-mb-4'>
                <Select
                    value={activityFilter}
                    options={activityOptions}
                    valueAs='id'
                    onChange={setActivityFilter}
                />
            </div>
            <DataWrapper isLoading={isLoading} hideContentOnLoading={false} noData={!statistic}>
                {statistic &&
                    <div className='tw-flex tw-flex-col tw-gap-4'>
                        <div className='tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-5'>
                            {map(metrics, ({metric, title, color, value, percent}) => (
                                <ActivityItem
                                    key={title}
                                    viewType='card'
                                    title={title}
                                    color={color}
                                    selected={includes(selectedMetrics, metric)}
                                    content={
                                        <div className='tw-text-base sm:tw-ts-h5'>
                                            <span className='tw-font-bold'>{value}</span>
                                            {percent !== undefined && <span>{` (${percent}%)`}</span>}
                                        </div>
                                    }
                                    onClick={() => toggleMetric(metric)}
                                />
                            ))}
                        </div>
                        <Card>
                            <div className='tw-ts-sub-header tw-mb-4'>{t(T_KEYS.DAILY_CHART.TITLE)}</div>
                            <div className='tw-h-72'>
                                <Chart
                                    data={chartData}
                                    margin={{top: 20, right: 20, left: -10, bottom: 5}}
                                    grid={{stroke: themeConfig.colors.lightenGray}}
                                    xAxis={{dataKey: 'date'}}
                                    yAxis={{allowDecimals: false}}
                                    lines={lines}
                                    tooltip={{content: <DailyChecksTooltip />}}
                                />
                            </div>
                        </Card>
                        {!isEmpty(rules) &&
                            <Card>
                                <div className='tw-ts-sub-header tw-mb-4'>{t(T_KEYS.RULES.TITLE)}</div>
                                {map(rules, (rule) => (
                                    <div key={rule.rule} className='tw-flex tw-items-center tw-gap-3 tw-mt-3'>
                                        <div className='tw-w-1/3 tw-truncate' title={rule.rule}>{rule.rule}</div>
                                        <div
                                            className='tw-flex-1 tw-h-2 tw-rounded tw-overflow-hidden'
                                            style={{backgroundColor: themeConfig.colors.lightenGray}}
                                        >
                                            <div
                                                className='tw-flex tw-h-2 tw-rounded tw-overflow-hidden'
                                                style={{width: `${getPercentage(rule.flagged, maxRuleFlagged)}%`}}
                                            >
                                                {rule.accepted > 0 &&
                                                    <div style={{backgroundColor: COLORS.PASSED, flexGrow: rule.accepted}} />}
                                                {rule.ignored > 0 &&
                                                    <div style={{backgroundColor: COLORS.FLAGGED, flexGrow: rule.ignored}} />}
                                                {rule.dismissed > 0 &&
                                                    <div style={{backgroundColor: COLORS.SKIPPED, flexGrow: rule.dismissed}} />}
                                            </div>
                                        </div>
                                        <div className='tw-ts-base-bold tw-w-10 tw-text-right'>{rule.flagged}</div>
                                        <div className='tw-ts-code tw-text-default-dimmed tw-whitespace-nowrap -md:tw-hidden'>
                                            {`${t(T_KEYS.DECISION.ACCEPTED)} ${rule.accepted}` +
                                                ` · ${t(T_KEYS.DECISION.IGNORED)} ${rule.ignored}` +
                                                ` · ${t(T_KEYS.DECISION.DISMISSED)} ${rule.dismissed}`}
                                        </div>
                                    </div>
                                ))}
                            </Card>}
                        <Card>
                            <div className='tw-ts-sub-header tw-mb-4'>{t(T_KEYS.AGENTS.TITLE)}</div>
                            <div className='tw-overflow-x-auto'>
                                <Table headers={agentsHeaders} noData={isEmpty(statistic.agents)} className='tw-min-w-196'>
                                    <TableBody className='tw-text-default'>
                                        {map(statistic.agents, (agent) => (
                                            <TableRow key={agent.agentEid}>
                                                <TableCell ellipsis>
                                                    {complianceUtils.getAgentName(t, users, agent.agentEid, agent.agentEmail)}
                                                </TableCell>
                                                {isAudioOnly && <TableCell>{agent.flagged}</TableCell>}
                                                {!isAudioOnly &&
                                                    <Fragment>
                                                        <TableCell>{agent.checks}</TableCell>
                                                        <TableCell>
                                                            {`${agent.flagged} (${getPercentage(agent.flagged, agent.checks)}%)`}
                                                        </TableCell>
                                                        <TableCell>
                                                            {`${agent.ignored} (${getPercentage(agent.ignored, agent.flagged)}%)`}
                                                        </TableCell>
                                                        <TableCell>
                                                            {`${agent.errors} (${getPercentage(agent.errors, agent.checks)}%)`}
                                                        </TableCell>
                                                    </Fragment>}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </Card>
                    </div>}
            </DataWrapper>
        </div>
    );
};
